import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, ChevronDown, LogOut, Menu, PanelLeft, Search, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/features/auth/useAuth';
import { ROUTES } from '@/app/router/routes';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { notificationsApi } from '@/services/api/endpoints/notifications';
import { queryKeys } from '@/services/api/queryKeys';
import { PERMISSIONS } from '@/types/permissions';
import type { AppNotification } from '@/types/notification';
import { formatDateTime } from '@/utils/format';
import { Breadcrumb } from './Breadcrumb';
import { ContextBar } from './ContextBar';
import { ThemeToggle } from './ThemeToggle';

/** The panel shows the latest few; the bell is a glance, not an inbox. */
const PANEL_SIZE = 10;

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/**
 * "3 min ago", "yesterday". Past a week the absolute date reads better than
 * "5 weeks ago", so it falls back to that.
 */
function relativeTime(value: string): string {
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 60) {
    return 'just now';
  }
  if (abs < 3600) {
    return RELATIVE.format(Math.round(seconds / 60), 'minute');
  }
  if (abs < 86_400) {
    return RELATIVE.format(Math.round(seconds / 3600), 'hour');
  }
  if (abs < 7 * 86_400) {
    return RELATIVE.format(Math.round(seconds / 86_400), 'day');
  }
  return formatDateTime(value);
}

const ICON_BUTTON =
  'flex h-9 w-9 items-center justify-center border border-line text-muted ' +
  'transition-colors hover:bg-surface hover:text-ink';

export function Header({
  onToggleSidebar,
  onToggleCollapse,
  onOpenSearch,
}: {
  onToggleSidebar: () => void;
  onToggleCollapse: () => void;
  onOpenSearch: () => void;
}) {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const readsNotifications = can(PERMISSIONS.NOTIFICATION_SELF_VIEW);
  const [signingOut, setSigningOut] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape — the two ways users expect to dismiss
  // a menu without choosing anything from it.
  useEffect(() => {
    if (!menuOpen && !notifOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current !== null && !menuRef.current.contains(target)) {
        setMenuOpen(false);
      }
      if (notifRef.current !== null && !notifRef.current.contains(target)) {
        setNotifOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen, notifOpen]);

  /*
   * The count polls once a minute and on focus, so a tab left open picks up
   * new items without a reload. The list itself is only fetched while the
   * panel is open — nobody reads it otherwise.
   */
  const unread = useQuery({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: notificationsApi.unreadCount,
    enabled: readsNotifications,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const feedParams = { page: 0, size: PANEL_SIZE };
  const feed = useQuery({
    queryKey: queryKeys.notifications.feed(feedParams),
    queryFn: () => notificationsApi.feed(feedParams),
    enabled: readsNotifications && notifOpen,
  });

  const refreshNotifications = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: refreshNotifications,
    onError: (error) => handleApiError(error),
  });

  const markAllRead = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: (result) => {
      toast.success(
        result.marked === 1 ? '1 notification marked read' : `${result.marked} notifications marked read`,
      );
      refreshNotifications();
    },
    onError: (error) => handleApiError(error),
  });

  /** Opening an item reads it; following its link is the point of opening it. */
  const openNotification = (notification: AppNotification) => {
    if (notification.readAt === undefined) {
      markRead.mutate(notification.id);
    }
    if (notification.link !== undefined && notification.link !== '') {
      setNotifOpen(false);
      navigate(notification.link);
    }
  };

  const unreadCount = unread.data?.unread ?? 0;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
      navigate(ROUTES.login, { replace: true });
    } finally {
      setSigningOut(false);
    }
  };

  const initials = `${user?.firstName.charAt(0) ?? ''}${user?.lastName.charAt(0) ?? ''}`;

  return (
    <header className="sticky top-0 z-30 shrink-0 border-b border-line bg-card/90 backdrop-blur">
      <div className="flex items-center gap-2 px-4 py-[11px] sm:gap-3.5 lg:px-[30px]">
        <button
          type="button"
          onClick={onToggleSidebar}
          className={`${ICON_BUTTON} min-[900px]:hidden`}
          aria-label="Open navigation"
        >
          <Menu className="h-4 w-4" aria-hidden="true" />
        </button>

        <button
          type="button"
          onClick={onToggleCollapse}
          className={`${ICON_BUTTON} hidden min-[900px]:flex`}
          aria-label="Collapse navigation"
        >
          <PanelLeft className="h-4 w-4" aria-hidden="true" />
        </button>

        <Breadcrumb />

        <div className="min-w-0 flex-1" />

        <button
          type="button"
          onClick={onOpenSearch}
          className="hidden items-center gap-2.5 rounded-lg border border-line bg-surface px-2.5 py-[7px] text-[13px] text-muted transition-colors hover:border-accent sm:flex sm:min-w-[230px]"
        >
          <Search className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />
          <span className="flex-1 text-left">Search…</span>
          <span className="border border-line px-1.5 text-[11px] tracking-[0.04em]">⌘K</span>
        </button>

        {readsNotifications && (
          <div className="relative" ref={notifRef}>
            <button
              type="button"
              onClick={() => {
                setNotifOpen((open) => !open);
                setMenuOpen(false);
              }}
              className={`${ICON_BUTTON} relative`}
              aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
              aria-haspopup="dialog"
              aria-expanded={notifOpen}
            >
              <Bell className="h-4 w-4" aria-hidden="true" />
              {unreadCount > 0 && (
                <span
                  className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center bg-accent px-1 text-[10px] font-bold tabular-nums text-accent-fg"
                  aria-hidden="true"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div
                role="dialog"
                aria-label="Notifications"
                className="animate-rise absolute right-0 top-11 z-40 w-[min(330px,calc(100vw-32px))] rounded-xl border border-line bg-card shadow-lg"
              >
                <div className="flex items-center justify-between gap-2 border-b-2 border-line px-3.5 py-3">
                  <strong className="text-[13px] font-extrabold text-ink">Notifications</strong>
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={() => markAllRead.mutate()}
                      disabled={markAllRead.isPending}
                      className="text-[12px] font-semibold text-accent hover:underline disabled:opacity-60"
                    >
                      {markAllRead.isPending ? 'Marking…' : 'Mark all read'}
                    </button>
                  )}
                </div>

                {feed.isPending ? (
                  <p className="px-3.5 py-8 text-center text-[13px] text-muted">Loading…</p>
                ) : feed.isError ? (
                  <div className="px-3.5 py-8 text-center text-[13px] text-muted">
                    <p>Notifications could not be loaded.</p>
                    <button
                      type="button"
                      onClick={() => void feed.refetch()}
                      className="mt-2 font-semibold text-accent hover:underline"
                    >
                      Try again
                    </button>
                  </div>
                ) : feed.data.data.length === 0 ? (
                  <p className="px-3.5 py-8 text-center text-[13px] text-muted">No notifications</p>
                ) : (
                  <ul className="max-h-[420px] divide-y divide-line overflow-y-auto">
                    {feed.data.data.map((notification) => {
                      const isUnread = notification.readAt === undefined;
                      return (
                        <li key={notification.id}>
                          <button
                            type="button"
                            onClick={() => openNotification(notification)}
                            className={`flex w-full gap-2.5 px-3.5 py-2.5 text-left transition-colors hover:bg-surface ${
                              isUnread ? 'bg-surface/60' : ''
                            }`}
                          >
                            <span
                              className={`mt-1.5 h-2 w-2 shrink-0 ${isUnread ? 'bg-accent' : 'bg-transparent'}`}
                              aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1">
                              <span
                                className={`block truncate text-[13px] ${
                                  isUnread ? 'font-bold text-ink' : 'text-muted'
                                }`}
                              >
                                {isUnread && <span className="sr-only">Unread: </span>}
                                {notification.title}
                              </span>
                              {notification.body !== undefined && notification.body !== '' && (
                                <span className="line-clamp-2 block text-[12px] text-muted">
                                  {notification.body}
                                </span>
                              )}
                              <time
                                dateTime={notification.createdAt}
                                title={formatDateTime(notification.createdAt)}
                                className="mt-0.5 block text-[11px] text-subtle"
                              >
                                {relativeTime(notification.createdAt)}
                              </time>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        <ThemeToggle />

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => {
              setMenuOpen((open) => !open);
              setNotifOpen(false);
            }}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="flex items-center gap-2.5 rounded-lg border border-line py-1 pl-1 pr-2 transition-colors hover:bg-surface"
          >
            <span
              className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-[11px] font-bold text-bg"
              aria-hidden="true"
            >
              {initials}
            </span>
            <span className="hidden text-left leading-[1.15] sm:block">
              <span className="block text-[12.5px] font-semibold text-ink">{user?.fullName}</span>
              <span className="block text-[11px] capitalize text-muted">
                {user?.roles[0]?.replaceAll('_', ' ').toLowerCase() ?? user?.email}
              </span>
            </span>
            <ChevronDown className="hidden h-3.5 w-3.5 text-muted sm:block" aria-hidden="true" />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="animate-rise absolute right-0 top-11 z-40 w-[250px] rounded-xl border border-line bg-card shadow-lg"
            >
              <div className="border-b-2 border-line px-3.5 py-3">
                <p className="truncate text-sm font-semibold text-ink">{user?.fullName}</p>
                <p className="truncate text-xs text-muted">{user?.email}</p>
                <div className="mt-2 flex flex-wrap items-center gap-1">
                  <ShieldCheck className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
                  {(user?.roles ?? []).map((role) => (
                    <span key={role} className="text-[11px] text-muted">
                      {role.replaceAll('_', ' ').toLowerCase()}
                    </span>
                  ))}
                </div>
              </div>
              <button
                type="button"
                role="menuitem"
                onClick={handleSignOut}
                disabled={signingOut}
                className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] text-ink transition-colors hover:bg-surface disabled:opacity-60"
              >
                <LogOut className="h-4 w-4 text-muted" aria-hidden="true" />
                {signingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Inside the sticky header so it stays put while a long list scrolls
          under it — the year you are looking at should never scroll away. */}
      <ContextBar />
    </header>
  );
}
